import { useCallback, useMemo } from 'react';
import PatientLayout from '@/components/patient/PatientLayout';
import type { PageProps } from './Show';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { TimelineFeedItem } from '@/components/timeline/TimelineFeedItem';
import type { Sentiment } from '@/components/timeline/SentimentPill';
import { EmptyState } from '@/components/ui/empty-state';
import { MessageSquare } from 'lucide-react';
import { TimelineDayGroup } from '@/components/timeline/TimelineDayGroup';
import { getCalendarBadgeParts, groupItemsByDay } from '@/lib/date';

export type ChatEntry = {
  id: number;
  title: string;
  started_at?: string | null;
  last_message_at?: string | null;
  updated_at?: string | null;
  messages_count: number;
  private: boolean;
  summary_present: boolean;
  chat_type: string;
  preview?: string | null;
  sentiment?: Sentiment | null;
};

export type ChatsPagination = {
  current_page: number;
  total_pages: number;
  total_count: number;
  next_page?: number | null;
  prev_page?: number | null;
};

type ChatsPageProps = PageProps & { chats: ChatEntry[]; pagination?: ChatsPagination };

export default function Chats(props: ChatsPageProps) {
  const { patient, stats, charts, chats, pagination } = props;

  return (
    <PatientLayout patient={patient} stats={stats} charts={charts} activeTab="chat">
      <ChatsList
        patient={patient}
        chats={chats}
        pagination={pagination}
      />
    </PatientLayout>
  );
}

type PatientSummary = {
  id: number;
  name?: string;
  display_name?: string;
};

type Pagination = ChatsPagination;

type ChatsListProps = {
  patient: PatientSummary;
  chats: ChatEntry[];
  pagination?: Pagination;
};

const timeFormatter = new Intl.DateTimeFormat('it-IT', { hour: '2-digit', minute: '2-digit' });

function ChatsList({ patient, chats, pagination }: ChatsListProps) {
  const safePagination: Pagination = pagination || {
    current_page: 1,
    total_pages: 1,
    total_count: chats.length,
    next_page: null,
    prev_page: null,
  };

  const goToPage = useCallback(
    (page: number) => {
      if (page === safePagination.current_page || page < 1 || page > safePagination.total_pages)
        return;
      window.location.href = `/therapists/patients/${patient.id}/chats?page=${page}`;
    },
    [safePagination.current_page, safePagination.total_pages, patient.id],
  );

  const grouped = useMemo(
    () => groupItemsByDay(chats, (chat) => getChatTimestamp(chat)),
    [chats],
  );

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div className="space-y-5">
          {grouped.length === 0 ? (
            <Card className="p-6">
              <EmptyState
                title="Nessuna chat disponibile."
                description="Quando il cliente avvia una chat, la troverai qui pronta da consultare."
                icon={<MessageSquare className="h-8 w-8" aria-hidden />}
              />
            </Card>
          ) : (
            <div className="relative grid grid-cols-1 gap-6">
              {grouped.map((group) => {
                const badge = getCalendarBadgeParts(group.date);
                const dayNumber = badge?.day ?? '--';
                const monthLabel = badge?.month ?? '—';
                return (
                  <TimelineDayGroup key={group.key} day={dayNumber} month={monthLabel}>
                    {group.items.map((chat) => (
                      <ChatFeedItem key={chat.id} chat={chat} />
                    ))}
                  </TimelineDayGroup>
                );
              })}
            </div>
          )}
        </div>

        {safePagination.total_pages > 1 && (
          <div className="flex justify-center gap-2 pt-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={!safePagination.prev_page}
              onClick={() => safePagination.prev_page && goToPage(safePagination.prev_page)}
            >
              &laquo;
            </Button>
            {Array.from({ length: safePagination.total_pages }, (_, index) => index + 1).map(
              (page) => (
                <Button
                  key={page}
                  size="sm"
                  variant={page === safePagination.current_page ? 'default' : 'ghost'}
                  onClick={() => goToPage(page)}
                  disabled={page === safePagination.current_page}
                >
                  {page}
                </Button>
              ),
            )}
            <Button
              variant="ghost"
              size="sm"
              disabled={!safePagination.next_page}
              onClick={() => safePagination.next_page && goToPage(safePagination.next_page)}
            >
              &raquo;
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}

function getChatTimestamp(chat: ChatEntry) {
  return chat.last_message_at || chat.started_at || chat.updated_at || null;
}

type ChatFeedItemProps = {
  chat: ChatEntry;
};

function ChatFeedItem({ chat }: ChatFeedItemProps) {
  const timestamp = getChatTimestamp(chat);
  const parsed = timestamp ? new Date(timestamp) : null;
  const timeLabel =
    parsed && !Number.isNaN(parsed.getTime()) ? timeFormatter.format(parsed) : '--:--';

  return (
    <TimelineFeedItem
      tone="chat"
      timeLabel={timeLabel}
      label="Chat"
      summary={chat.title}
      href={`/therapists/chats/${chat.id}`}
      sentiment={chat.sentiment}
      privacyLabel={chat.private ? 'Privata' : 'Pubblica'}
    />
  );
}
